import { useRef, useEffect, useCallback } from 'react';
import { clientsData } from '@/mocks/clients';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const CYCLE_DURATION = 35000;

export default function HappyClientsSection() {
  const trackRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(0);
  const speedRef = useRef(0.6);
  const halfWidthRef = useRef(0);
  const isPausedRef = useRef(false);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const dragStartPosRef = useRef(0);
  const rafRef = useRef(0);
  const { ref: sectionRef, isVisible } = useScrollAnimation(0.05);

  const updateDimensions = useCallback(() => {
    if (!trackRef.current) return;
    const half = trackRef.current.scrollWidth / 2;
    halfWidthRef.current = half;
    speedRef.current = half / (CYCLE_DURATION / 16.6667);
    if (speedRef.current < 0.3) speedRef.current = 0.3;
  }, []);

  const animate = useCallback(() => {
    if (!trackRef.current) {
      rafRef.current = requestAnimationFrame(animate);
      return;
    }
    if (isPausedRef.current || isDraggingRef.current) {
      rafRef.current = requestAnimationFrame(animate);
      return;
    }
    posRef.current -= speedRef.current;
    const half = halfWidthRef.current;
    if (half > 0 && Math.abs(posRef.current) >= half) {
      posRef.current = 0;
    }
    trackRef.current.style.transform = `translate3d(${posRef.current}px, 0, 0)`;
    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    if (!trackRef.current) return;
    updateDimensions();
    const ro = new ResizeObserver(updateDimensions);
    ro.observe(trackRef.current);
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [updateDimensions, animate]);

  const handleMouseEnter = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  const handleMouseLeave = useCallback(() => {
    isPausedRef.current = false;
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    isDraggingRef.current = true;
    startXRef.current = e.touches[0].clientX;
    dragStartPosRef.current = posRef.current;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDraggingRef.current || !trackRef.current) return;
    const delta = e.touches[0].clientX - startXRef.current;
    let newPos = dragStartPosRef.current + delta;
    const half = halfWidthRef.current;
    if (half > 0) {
      newPos = ((newPos % half) + half) % half;
      if (newPos > 0) newPos -= half;
    }
    posRef.current = newPos;
    trackRef.current.style.transform = `translate3d(${newPos}px, 0, 0)`;
  }, []);

  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  const logos = clientsData;
  const duplicatedLogos = [...logos, ...logos];

  return (
    <section id="testimonials" className="py-16 md:py-24 bg-background-50 overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-10">
        <div
          ref={sectionRef}
          className={`max-w-6xl mx-auto transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="text-center mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-50 border border-accent-200/70 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
              <span className="text-xs font-medium text-accent-700 uppercase tracking-wider">Clients</span>
            </div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4">
              Our Happy Clients
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              Trusted by Businesses Across Multiple Industries
            </p>
          </div>
        </div>
      </div>

      <div
        className="relative w-full overflow-hidden select-none"
        style={{ touchAction: 'pan-y' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          ref={trackRef}
          className="flex items-center will-change-transform"
          style={{ width: 'max-content' }}
        >
          {duplicatedLogos.map((client, index) => (
            <div
              key={`${client.id}-${index}`}
              className="flex-shrink-0 px-3 md:px-4"
            >
              <div className="w-[140px] h-[90px] md:w-[180px] md:h-[110px] rounded-xl bg-white border border-background-200/70 flex items-center justify-center p-3 md:p-4 shadow-sm transition-all duration-300 hover:shadow-md hover:border-background-300/60">
                <img
                  src={client.logo}
                  alt={client.name}
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                  draggable={false}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}